import React, { useEffect, useState, useCallback } from "react";
import { Check, X, RefreshCw, Eye } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";

const kycStyle = {
  unverified: "text-white/45 bg-white/5",
  pending: "text-amber-400 bg-amber-400/10",
  verified: "text-emerald-400 bg-emerald-400/10",
  rejected: "text-rose-400 bg-rose-400/10",
};

const AdminKYC = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null); // user_id
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/kyc", { params: { status: statusFilter } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (uid) => {
    setViewing(uid);
    setDetail(null);
    setRejectReason("");
    try {
      const { data } = await api.get(`/admin/kyc/${uid}`);
      setDetail(data);
    } catch { /* noop */ }
  };

  const approve = async () => {
    if (!viewing) return;
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${viewing}/approve`, { reason: null });
      setViewing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!viewing) return;
    if (rejectReason.trim().length < 2) { alert("Reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${viewing}/reject`, { reason: rejectReason.trim() });
      setViewing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout title="KYC" subtitle="Verification queue">
      <div className="bb-card rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-white/5">
          <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-1 border border-white/8">
            {["pending", "verified", "rejected", "unverified", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-[12px] rounded-md whitespace-nowrap capitalize transition-colors ${
                  statusFilter === s ? "bg-white text-[#07080d] font-medium" : "text-white/55 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <button onClick={load} className="w-9 h-9 rounded-lg border border-white/8 text-white/70 hover:text-white flex items-center justify-center">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[960px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-white/40 font-semibold bg-white/[0.02]">
                <th className="text-left py-3 px-5">User</th>
                <th className="text-left py-3">Method</th>
                <th className="text-left py-3">Doc type</th>
                <th className="text-left py-3">Doc / NIN number</th>
                <th className="text-left py-3">Submitted</th>
                <th className="text-left py-3">Status</th>
                <th className="text-right py-3 pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.user.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="py-3.5 px-5">
                    <div className="text-white font-medium">{row.user.name}</div>
                    <div className="text-white/45 text-[11px]">{row.user.email}</div>
                  </td>
                  <td>
                    {row.submission?.method === "nin" ? (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded text-[#7d8eff] bg-[#7d8eff]/10">
                        NIN
                      </span>
                    ) : row.submission ? (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded text-white/70 bg-white/[0.06]">
                        Document
                      </span>
                    ) : (
                      <span className="text-white/30 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="text-white/70 capitalize">{row.submission?.doc_type === "nin" ? "—" : row.submission?.doc_type?.replace(/_/g, " ") || "—"}</td>
                  <td className="text-white/70 font-mono text-[12px]">{row.submission?.doc_number || "—"}</td>
                  <td className="text-white/65 text-[11px]">
                    {row.submission?.submitted_at ? new Date(row.submission.submitted_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${kycStyle[row.user.kyc_status] || kycStyle.unverified}`}>
                      {row.user.kyc_status}
                    </span>
                  </td>
                  <td className="text-right pr-5">
                    {row.submission && (
                      <button onClick={() => openDetail(row.user.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-white/10 text-white/70 hover:text-white text-[11px]">
                        <Eye size={11} /> Review
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && items.length === 0 && (
          <div className="py-16 text-center text-white/40 text-[13px]">No submissions to review.</div>
        )}
      </div>

      {viewing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setViewing(null)}></div>
          <div className="relative bb-card rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-white/5 flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-[18px]">Review KYC submission</div>
                <div className="text-[12px] text-white/55">{detail?.user?.email}</div>
              </div>
              <button onClick={() => setViewing(null)} className="w-8 h-8 rounded-lg text-white/55 hover:text-white"><X size={17} className="mx-auto" /></button>
            </div>
            <div className="p-5 space-y-4">
              {!detail ? (
                <div className="text-[12px] text-white/45">Loading…</div>
              ) : !detail.submission ? (
                <div className="text-[12px] text-white/45">No submission found.</div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <div className="text-[11px] text-white/55 mb-0.5">Method</div>
                      <div className="text-[14px] text-white capitalize">
                        {detail.submission.method === "nin" ? "NIN only" : "ID document"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-white/55 mb-0.5">
                        {detail.submission.method === "nin" ? "Doc type" : "Document type"}
                      </div>
                      <div className="text-[14px] text-white capitalize">
                        {detail.submission.doc_type === "nin" ? "—" : detail.submission.doc_type?.replace(/_/g, " ")}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-white/55 mb-0.5">
                        {detail.submission.method === "nin" ? "NIN number" : "Document number"}
                      </div>
                      <div className="text-[14px] text-white font-mono tabular-nums">{detail.submission.doc_number}</div>
                    </div>
                  </div>
                  {detail.submission.method === "nin" ? (
                    <div className="px-4 py-4 rounded-lg bg-[#7d8eff]/8 border border-[#7d8eff]/20 text-[12px] text-white/70 leading-relaxed">
                      User chose to verify with their <strong className="text-white">11-digit NIN</strong> only. No document image was uploaded.
                      Cross-check the NIN against the National Identity database before approving.
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <div className="text-[11px] text-white/55 mb-1.5">ID document</div>
                        {detail.submission.doc_image_b64 ? (
                          <img src={`data:image/jpeg;base64,${detail.submission.doc_image_b64.replace(/^data:.*;base64,/, "")}`} alt="ID" className="w-full rounded-lg border border-white/10" />
                        ) : (
                          <div className="text-[12px] text-white/40 italic px-4 py-6 rounded-lg border border-dashed border-white/10 text-center">Not provided</div>
                        )}
                      </div>
                      <div>
                        <div className="text-[11px] text-white/55 mb-1.5">Proof of address</div>
                        {detail.submission.address_image_b64 ? (
                          <img src={`data:image/jpeg;base64,${detail.submission.address_image_b64.replace(/^data:.*;base64,/, "")}`} alt="Address" className="w-full rounded-lg border border-white/10" />
                        ) : (
                          <div className="text-[12px] text-white/40 italic px-4 py-6 rounded-lg border border-dashed border-white/10 text-center">Not provided</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-[11px] text-white/55 mb-1.5">Reject reason (only if rejecting)</div>
                    <input
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="e.g. NIN doesn't match records, please resubmit"
                      className="w-full h-10 px-3 bg-white/[0.02] border border-white/10 rounded-lg text-[12px] text-white outline-none focus:border-[#ff7a1a] placeholder:text-white/30"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="p-5 border-t border-white/5 flex justify-end gap-2">
              <button onClick={reject} disabled={busy || !detail?.submission} className="px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[13px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                <X size={13} /> Reject
              </button>
              <button onClick={approve} disabled={busy || !detail?.submission} className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                <Check size={13} /> Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminKYC;
